// features/inventory/pages/LocationManagementPage.tsx
/**
 * Location Management Page — Manage warehouse locations (zones, aisles, racks, shelves, bins).
 *
 * Route: /inventory/locations
 *
 * Features:
 * - List all locations with hierarchy
 * - Filter by branch and location type
 * - Create, edit, and deactivate locations
 * - Visual location type badges
 * - Search by code or name
 * - Stock count per location
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { getBranches } from '@/features/settings/services/settingsApi';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft,
    Plus,
    Search,
    X,
    MapPin,
    Store,
    Package,
    RefreshCw,
    Save,
    Trash2,
    Warehouse,
    Grid3X3,
    Rows3,
    Box,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

type LocationType = 'warehouse' | 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin';

interface StockLocation {
    id: string;
    tenantId: string;
    branchId: string;
    code: string;
    name: string;
    locationType: LocationType;
    parentId: string | null;
    description: string | null;
    isActive: boolean;
    capacity: string | null;
    createdAt: string;
    updatedAt: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const LOCATION_TYPE_CONFIG: Record<LocationType, { label: string; icon: React.ElementType; color: string }> = {
    warehouse: { label: 'Warehouse', icon: Warehouse, color: 'bg-slate-100 text-slate-700' },
    zone: { label: 'Zone', icon: MapPin, color: 'bg-blue-100 text-blue-700' },
    aisle: { label: 'Aisle', icon: Rows3, color: 'bg-purple-100 text-purple-700' },
    rack: { label: 'Rack', icon: Grid3X3, color: 'bg-amber-100 text-amber-700' },
    shelf: { label: 'Shelf', icon: Rows3, color: 'bg-emerald-100 text-emerald-700' },
    bin: { label: 'Bin', icon: Box, color: 'bg-rose-100 text-rose-700' },
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function LocationManagementPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // State
    const [branchId, setBranchId] = useState('');
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<LocationType | 'all'>('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState<StockLocation | null>(null);

    // Form state
    const [formCode, setFormCode] = useState('');
    const [formName, setFormName] = useState('');
    const [formType, setFormType] = useState<LocationType>('bin');
    const [formDescription, setFormDescription] = useState('');
    const [formCapacity, setFormCapacity] = useState('');

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-locations', user?.tenantId],
        queryFn: async () => {
            const data = await getBranches(user!.tenantId);
            return Array.isArray(data) ? data : [];
        },
        enabled: !!user?.tenantId,
    });

    const { data: locationsResponse, isLoading, refetch } = useQuery({
        queryKey: ['locations', user?.tenantId, branchId, typeFilter, search],
        queryFn: async () => {
            const response = await apiClient.get(
                `/tenants/${user!.tenantId}/stock/locations`,
                {
                    params: {
                        branchId: branchId || undefined,
                        locationType: typeFilter !== 'all' ? typeFilter : undefined,
                        search: search || undefined,
                        limit: 100,
                        sortBy: 'code',
                        order: 'asc',
                    },
                }
            );
            return response.data?.data || [];
        },
        enabled: !!user?.tenantId,
    });

    // Mutations
    const createMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.post(
                `/tenants/${user!.tenantId}/stock/locations`,
                {
                    branchId,
                    code: formCode,
                    name: formName,
                    locationType: formType,
                    description: formDescription || undefined,
                    capacity: formCapacity || undefined,
                }
            );
            return response.data;
        },
        onSuccess: () => {
            addToast({ type: 'success', title: 'Location created' });
            setShowCreateModal(false);
            resetForm();
            refetch();
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed to create',
                message: err?.response?.data?.error?.message || err?.message,
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async () => {
            if (!editingLocation) return;
            const response = await apiClient.patch(
                `/tenants/${user!.tenantId}/stock/locations/${editingLocation.id}`,
                {
                    code: formCode,
                    name: formName,
                    locationType: formType,
                    description: formDescription || undefined,
                    capacity: formCapacity || undefined,
                }
            );
            return response.data;
        },
        onSuccess: () => {
            addToast({ type: 'success', title: 'Location updated' });
            setEditingLocation(null);
            resetForm();
            refetch();
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed to update',
                message: err?.response?.data?.error?.message || err?.message,
            });
        },
    });

    const deactivateMutation = useMutation({
        mutationFn: async (locationId: string) => {
            await apiClient.patch(
                `/tenants/${user!.tenantId}/stock/locations/${locationId}`,
                { isActive: false }
            );
        },
        onSuccess: () => {
            addToast({ type: 'success', title: 'Location deactivated' });
            refetch();
        },
        onError: (err: any) => {
            addToast({ type: 'error', title: 'Failed to deactivate', message: err?.message });
        },
    });

    // Derived data
    const branchesList = (branches || []) as Array<{ id: string; name: string }>;
    const locations = (locationsResponse || []) as StockLocation[];

    const resetForm = () => {
        setFormCode('');
        setFormName('');
        setFormType('bin');
        setFormDescription('');
        setFormCapacity('');
    };

    const openEditModal = (location: StockLocation) => {
        setEditingLocation(location);
        setFormCode(location.code);
        setFormName(location.name);
        setFormType(location.locationType);
        setFormDescription(location.description || '');
        setFormCapacity(location.capacity || '');
    };

    const getBranchName = (id: string) =>
        branchesList.find((b) => b.id === id)?.name || 'Unknown';

    const canSave = formCode.trim() && formName.trim() && branchId;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/inventory')}
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg shadow-indigo-500/25">
                                <MapPin className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Warehouse Locations
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {locations.length} locations ·{' '}
                                    {getBranchName(branchId)}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => refetch()}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <RefreshCw className="w-4 h-4" /> Refresh
                            </button>
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                disabled={!branchId}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 transition-all font-semibold shadow-lg text-sm disabled:opacity-50"
                                type="button"
                            >
                                <Plus className="w-4 h-4" /> Add Location
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto p-6 lg:p-8 space-y-5">
                {/* Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col lg:flex-row gap-1.5">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by code or name..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 text-sm"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <select
                            value={branchId}
                            onChange={(e) => setBranchId(e.target.value)}
                            className="px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white"
                        >
                            <option value="">Select Branch</option>
                            {branchesList.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {b.name}
                                </option>
                            ))}
                        </select>
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                            {[
                                { value: 'all' as const, label: 'All Types' },
                                ...Object.entries(LOCATION_TYPE_CONFIG).map(
                                    ([key, config]) => ({
                                        value: key as LocationType,
                                        label: config.label,
                                    })
                                ),
                            ].map((f) => (
                                <button
                                    key={f.value}
                                    onClick={() => setTypeFilter(f.value)}
                                    className={clsx(
                                        'px-3 py-2 rounded-lg text-xs font-medium transition-all',
                                        typeFilter === f.value
                                            ? 'bg-white text-indigo-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                    type="button"
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Locations Grid */}
                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : !branchId ? (
                    <EmptyState
                        icon={<Store className="w-16 h-16" />}
                        title="Select a Branch"
                        description="Choose a branch to view and manage its warehouse locations."
                    />
                ) : locations.length === 0 ? (
                    <EmptyState
                        icon={<MapPin className="w-16 h-16" />}
                        title="No locations configured"
                        description="Add warehouse locations to start tracking where stock is stored."
                        action={{
                            label: 'Add First Location',
                            onClick: () => setShowCreateModal(true),
                        }}
                    />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {locations.map((location) => {
                            const typeConfig = LOCATION_TYPE_CONFIG[location.locationType];
                            const TypeIcon = typeConfig.icon;

                            return (
                                <motion.div
                                    key={location.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    whileHover={{ y: -2 }}
                                    className={clsx(
                                        'bg-white rounded-2xl border-2 p-5 transition-all hover:shadow-lg',
                                        location.isActive
                                            ? 'border-slate-200 hover:border-indigo-300'
                                            : 'border-slate-100 bg-slate-50 opacity-60'
                                    )}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={clsx(
                                                    'p-2.5 rounded-xl',
                                                    typeConfig.color
                                                )}
                                            >
                                                <TypeIcon className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-sm text-slate-800">
                                                    {location.name}
                                                </h3>
                                                <p className="text-xs text-slate-400 font-mono">
                                                    {location.code}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge
                                            variant={location.isActive ? 'success' : 'neutral'}
                                            size="sm"
                                        >
                                            {location.isActive ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </div>
                                    {location.description && (
                                        <p className="text-xs text-slate-500 mb-3">
                                            {location.description}
                                        </p>
                                    )}
                                    <div className="flex items-center justify-between text-xs text-slate-400">
                                        <span>{typeConfig.label}</span>
                                        {location.capacity && (
                                            <span>Capacity: {parseFloat(location.capacity).toLocaleString()}</span>
                                        )}
                                    </div>
                                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                                        <button
                                            onClick={() => openEditModal(location)}
                                            className="flex-1 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                                            type="button"
                                        >
                                            Edit
                                        </button>
                                        {location.isActive && (
                                            <button
                                                onClick={() => {
                                                    if (
                                                        window.confirm(
                                                            `Deactivate ${location.name}?`
                                                        )
                                                    ) {
                                                        deactivateMutation.mutate(
                                                            location.id
                                                        );
                                                    }
                                                }}
                                                className="px-3 py-1.5 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors"
                                                type="button"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            <AnimatePresence>
                {(showCreateModal || editingLocation) && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => {
                            setShowCreateModal(false);
                            setEditingLocation(null);
                            resetForm();
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h2 className="text-lg font-bold text-slate-900 mb-4">
                                {editingLocation ? 'Edit Location' : 'Add Location'}
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                                        Code *
                                    </label>
                                    <input
                                        type="text"
                                        value={formCode}
                                        onChange={(e) => setFormCode(e.target.value)}
                                        placeholder="e.g., A01-R02-S03-B01"
                                        className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formName}
                                        onChange={(e) => setFormName(e.target.value)}
                                        placeholder="e.g., Aisle 1, Rack 2, Shelf 3, Bin 1"
                                        className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                                        Type
                                    </label>
                                    <select
                                        value={formType}
                                        onChange={(e) => setFormType(e.target.value as LocationType)}
                                        className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white"
                                    >
                                        {Object.entries(LOCATION_TYPE_CONFIG).map(
                                            ([key, config]) => (
                                                <option key={key} value={key}>
                                                    {config.label}
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        value={formDescription}
                                        onChange={(e) => setFormDescription(e.target.value)}
                                        rows={2}
                                        placeholder="Optional description..."
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                                        Capacity (Optional)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        value={formCapacity}
                                        onChange={(e) => setFormCapacity(e.target.value)}
                                        placeholder="Max quantity this location can hold"
                                        className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setEditingLocation(null);
                                        resetForm();
                                    }}
                                    className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm hover:bg-white transition"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (editingLocation) {
                                            updateMutation.mutate();
                                        } else {
                                            createMutation.mutate();
                                        }
                                    }}
                                    disabled={!canSave}
                                    className="flex-1 px-5 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold text-sm shadow-lg hover:from-indigo-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                    type="button"
                                >
                                    {createMutation.isPending || updateMutation.isPending ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    {editingLocation ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}